import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Container,
  Grid,
  Paper,
  MenuItem,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import {
  Delete,
  PictureAsPdf,
  Save,
  Search,
  Download,
} from "@mui/icons-material";
import { useAuth } from "../hooks/useAuth";
import { useToastStateSync } from "../hooks/useToastStateSync";
import ResearchService from "../services/ResearchService";
import type { AppResearchReport } from "../types/appResearch";
import type { CreateAppDTO } from "../types/app";

const APP_REGISTRATION_PREFILL_KEY = "app_registration_prefill";

const RESEARCH_MODELS = [
  { value: "", label: "Default (Gemini 2.5 Flash Lite)" },
  { value: "gemini-2.5-flash-lite", label: "Gemini 2.5 Flash Lite" },
  { value: "gemini-2.5-pro", label: "Gemini 2.5 Pro" },
  { value: "gemini-2.5-flash", label: "Gemini 2.5 Flash" },
  { value: "gpt-4.1-mini", label: "OpenAI GPT-4.1 Mini" },
];

export default function AppResearch() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [tab, setTab] = useState(0);
  const [appQuery, setAppQuery] = useState("");
  const [model, setModel] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [cloningReportId, setCloningReportId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  useToastStateSync({ success, error });
  const [currentReport, setCurrentReport] = useState<AppResearchReport | null>(
    null,
  );
  const [savedReports, setSavedReports] = useState<AppResearchReport[]>([]);

  const loadSavedReports = useCallback(async () => {
    if (!user?.email) return;
    try {
      const reports = await ResearchService.getAppResearchReports(user.email);
      setSavedReports(reports);
    } catch (loadError) {
      console.error(loadError);
      setError("Failed to load saved reports.");
    }
  }, [user?.email]);

  useEffect(() => {
    loadSavedReports();
  }, [loadSavedReports]);

  const handleRunResearch = async () => {
    if (!user?.email) return;
    if (!appQuery.trim()) {
      setError("Enter an app name or App Store/Play Store link.");
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      const report = await ResearchService.conductAppResearch(
        user.email,
        { appQuery: appQuery.trim() },
        model || undefined,
      );
      setCurrentReport(report);
      setTab(1);
      setSuccess("App research completed.");
    } catch (researchError: any) {
      console.error(researchError);
      setError(researchError?.message || "Failed to run app research.");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveReport = async () => {
    if (!currentReport) return;
    setSaving(true);
    setError(null);
    try {
      await ResearchService.saveAppResearchReport(currentReport);
      await loadSavedReports();
      setSuccess("Report saved.");
    } catch (saveError: any) {
      console.error(saveError);
      setError(saveError?.message || "Failed to save report.");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteReport = async (report: AppResearchReport) => {
    if (!user?.email) return;
    if (!window.confirm("Delete this saved report?")) return;
    try {
      await ResearchService.deleteAppResearchReport(
        report.reportId,
        user.email,
      );
      await loadSavedReports();
      if (currentReport?.reportId === report.reportId) {
        setCurrentReport(null);
      }
    } catch (deleteError) {
      console.error(deleteError);
      setError("Failed to delete report.");
    }
  };

  const handleExportJSON = (report: AppResearchReport) => {
    const blob = new Blob([JSON.stringify(report, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${report.appName.replace(
      /[^a-zA-Z0-9-_]/g,
      "_",
    )}-app-research.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleExportPDF = (report: AppResearchReport) => {
    const printWindow = window.open("", "_blank", "width=1024,height=900");
    if (!printWindow) {
      setError("Popup blocked. Allow popups to export PDF.");
      return;
    }

    const bullet = (items: string[]) =>
      items.length
        ? `<ul>${items
            .map((item) => `<li>${escapeHtml(item)}</li>`)
            .join("")}</ul>`
        : "<p>No data available.</p>";

    const citations = report.citations?.length
      ? `<ol>${report.citations
          .map(
            (citation) =>
              `<li><a href="${escapeHtml(citation.url)}">${escapeHtml(
                citation.title,
              )}</a>${
                citation.publisher ? ` (${escapeHtml(citation.publisher)})` : ""
              }${
                citation.publishedAt
                  ? ` - ${escapeHtml(citation.publishedAt)}`
                  : ""
              }${
                citation.note
                  ? `<br/><small>${escapeHtml(citation.note)}</small>`
                  : ""
              }</li>`,
          )
          .join("")}</ol>`
      : "<p>No citations available.</p>";

    const html = `
      <html>
        <head>
          <title>${escapeHtml(report.appName)} - App Research</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 24px; color: #111827; }
            h1 { margin-bottom: 8px; }
            h2 { margin-top: 20px; margin-bottom: 8px; font-size: 18px; }
            p, li { font-size: 14px; line-height: 1.5; }
            a { color: #1d4ed8; }
          </style>
        </head>
        <body>
          <h1>${escapeHtml(report.appName)} - App Research Report</h1>
          <p><strong>Query:</strong> ${escapeHtml(report.query.appQuery)}</p>
          <p><strong>Launch Date:</strong> ${escapeHtml(
            report.appLaunchDate || "Unknown",
          )}</p>
          <p><strong>Category:</strong> ${escapeHtml(
            report.category || "Unknown",
          )}</p>
          <p><strong>Summary:</strong> ${escapeHtml(report.summary)}</p>
          <h2>What the App Does</h2>
          <p>${escapeHtml(report.whatItDoes)}</p>
          <h2>Target Demographics</h2>
          ${bullet(report.targetDemographics)}
          <h2>Monetization</h2>
          ${bullet(report.monetization)}
          <h2>Key Features</h2>
          ${bullet(report.keyFeatures)}
          <h2>Growth Signals</h2>
          ${bullet(report.growthSignals)}
          <h2>Opportunities</h2>
          ${bullet(report.opportunities)}
          <h2>Risks</h2>
          ${bullet(report.risks)}
          <h2>Strategic Recommendations</h2>
          ${bullet(report.strategicRecommendations)}
          <h2>Source Citations</h2>
          ${citations}
        </body>
      </html>
    `;

    printWindow.document.open();
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  };

  const inferPlatform = (platforms: string[]): "android" | "ios" | "both" => {
    const normalized = platforms.map((item) => item.toLowerCase());
    const hasAndroid = normalized.some(
      (item) => item.includes("android") || item.includes("play"),
    );
    const hasIos = normalized.some(
      (item) => item.includes("ios") || item.includes("apple"),
    );
    if (hasAndroid && hasIos) return "both";
    if (hasIos) return "ios";
    return "android";
  };

  const deriveStoreId = (storeUrl?: string): string => {
    if (!storeUrl) return "";
    try {
      const url = new URL(storeUrl);
      if (url.hostname.includes("play.google.com")) {
        return url.searchParams.get("id") || "";
      }
      const idMatch = url.pathname.match(/id(\d+)/i);
      return idMatch?.[1] || "";
    } catch {
      return "";
    }
  };

  const buildRegistrationPrefill = (
    report: AppResearchReport,
  ): CreateAppDTO => {
    const platform = inferPlatform(report.platforms || []);
    const isAndroidUrl = Boolean(
      report.appStoreUrl?.includes("play.google.com"),
    );
    const isIosUrl = Boolean(report.appStoreUrl?.includes("apps.apple.com"));
    return {
      name: report.appName,
      description: report.whatItDoes || report.summary || "",
      category: report.category || "productivity",
      platform,
      storeId: deriveStoreId(report.appStoreUrl),
      storeUrl: report.appStoreUrl || "",
      androidStoreUrl:
        platform === "both" || platform === "android"
          ? isAndroidUrl
            ? report.appStoreUrl || ""
            : ""
          : "",
      iosStoreUrl:
        platform === "both" || platform === "ios"
          ? isIosUrl
            ? report.appStoreUrl || ""
            : ""
          : "",
      researchContext: {
        whatItDoes: report.whatItDoes,
        targetDemographics: report.targetDemographics,
        monetization: report.monetization,
        keyFeatures: report.keyFeatures,
        growthSignals: report.growthSignals,
        opportunities: report.opportunities,
        risks: report.risks,
        strategicRecommendations: report.strategicRecommendations,
        citations: report.citations,
      },
    };
  };

  const handleCloneToRegistration = async (report: AppResearchReport) => {
    setCloningReportId(report.reportId);
    setError(null);
    setSuccess(null);
    try {
      const basePrefill = buildRegistrationPrefill(report);
      const clonePrefill = await ResearchService.buildCloneRegistrationPrefill(
        report,
        basePrefill,
        model || undefined,
      );
      localStorage.setItem(
        APP_REGISTRATION_PREFILL_KEY,
        JSON.stringify(clonePrefill),
      );
      setSuccess("AI clone generated. Redirecting to App Registration...");
      navigate("/app-registration");
    } catch (cloneError: any) {
      console.error(cloneError);
      setError(cloneError?.message || "Failed to generate clone prefill.");
    } finally {
      setCloningReportId((current) =>
        current === report.reportId ? null : current,
      );
    }
  };

  return (
    <Container maxWidth={false} sx={{ py: 4 }}>
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
        App Research
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Enter an app name or store URL to run Google-search-grounded research
        and generate app insights you can save and revisit later.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert
          severity="success"
          sx={{ mb: 2 }}
          onClose={() => setSuccess(null)}
        >
          {success}
        </Alert>
      )}

      <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
        <Tabs value={tab} onChange={(_, value) => setTab(value)}>
          <Tab label="New Research" />
          <Tab label="Current Report" />
          <Tab label="Saved Reports" />
        </Tabs>
      </Box>

      {tab === 0 && (
        <Paper sx={{ p: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="App Name or Store URL"
                placeholder="e.g. Duolingo or https://play.google.com/store/apps/details?id=com.duolingo"
                value={appQuery}
                onChange={(event) => setAppQuery(event.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                select
                fullWidth
                label="Model (Optional)"
                value={model}
                onChange={(event) => setModel(event.target.value)}
              >
                {RESEARCH_MODELS.map((option) => (
                  <MenuItem
                    key={option.value || "default"}
                    value={option.value}
                  >
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <Button
                variant="contained"
                startIcon={<Search />}
                onClick={handleRunResearch}
                disabled={loading}
              >
                {loading ? "Researching..." : "Run App Research"}
              </Button>
            </Grid>
          </Grid>
        </Paper>
      )}

      {tab === 1 && (
        <>
          {!currentReport ? (
            <Alert severity="info">
              Run research first or open a saved report.
            </Alert>
          ) : (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Paper sx={{ p: 3 }}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      gap: 1,
                      alignItems: "center",
                    }}
                  >
                    <Box>
                      <Typography variant="h5" sx={{ fontWeight: 700 }}>
                        {currentReport.appName}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Launch: {currentReport.appLaunchDate || "Unknown"} |
                        Category: {currentReport.category || "Unknown"}
                      </Typography>
                    </Box>
                    <Box sx={{ display: "flex", gap: 1 }}>
                      <Button
                        variant="outlined"
                        startIcon={<Download />}
                        onClick={() => handleExportJSON(currentReport)}
                      >
                        JSON
                      </Button>
                      <Button
                        variant="outlined"
                        startIcon={<PictureAsPdf />}
                        onClick={() => handleExportPDF(currentReport)}
                      >
                        PDF
                      </Button>
                      <Button
                        variant="contained"
                        startIcon={<Save />}
                        onClick={handleSaveReport}
                        disabled={saving}
                      >
                        {saving ? "Saving..." : "Save Report"}
                      </Button>
                      <Button
                        variant="outlined"
                        disabled={cloningReportId === currentReport.reportId}
                        onClick={() => handleCloneToRegistration(currentReport)}
                      >
                        {cloningReportId === currentReport.reportId
                          ? "Cloning..."
                          : "Clone To Registration"}
                      </Button>
                    </Box>
                  </Box>
                  <Typography variant="body1" sx={{ mt: 2 }}>
                    {currentReport.summary}
                  </Typography>
                </Paper>
              </Grid>

              <Section
                title="What The App Does"
                items={[currentReport.whatItDoes]}
              />
              <Section
                title="Target Demographics"
                items={currentReport.targetDemographics}
              />
              <Section
                title="Monetization"
                items={currentReport.monetization}
              />
              <Section title="Key Features" items={currentReport.keyFeatures} />
              <Section
                title="Growth Signals"
                items={currentReport.growthSignals}
              />
              <Section
                title="Opportunities"
                items={currentReport.opportunities}
              />
              <Section title="Risks" items={currentReport.risks} />
              <CitationSection citations={currentReport.citations || []} />
              <Section
                title="Strategic Recommendations"
                items={currentReport.strategicRecommendations}
              />
            </Grid>
          )}
        </>
      )}

      {tab === 2 && (
        <>
          {savedReports.length === 0 ? (
            <Alert severity="info">No saved reports yet.</Alert>
          ) : (
            <Grid container spacing={2}>
              {savedReports.map((report) => (
                <Grid item xs={12} md={6} key={report.reportId}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6">{report.appName}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {report.savedAt?.toLocaleString() ||
                          report.metadata.generatedAt.toLocaleString()}
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        {report.summary}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Citations: {report.citations?.length || 0}
                      </Typography>
                    </CardContent>
                    <CardActions>
                      <Button
                        size="small"
                        onClick={() => {
                          setCurrentReport(report);
                          setTab(1);
                        }}
                      >
                        Open
                      </Button>
                      <Button
                        size="small"
                        disabled={cloningReportId === report.reportId}
                        onClick={() => handleCloneToRegistration(report)}
                      >
                        {cloningReportId === report.reportId
                          ? "Cloning..."
                          : "Clone"}
                      </Button>
                      <Button
                        size="small"
                        color="error"
                        startIcon={<Delete />}
                        onClick={() => handleDeleteReport(report)}
                      >
                        Delete
                      </Button>
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </>
      )}
    </Container>
  );
}

function Section({ title, items }: { title: string; items: string[] }) {
  return (
    <Grid item xs={12} md={6}>
      <Paper sx={{ p: 2.5, height: "100%" }}>
        <Typography variant="h6" sx={{ mb: 1.2 }}>
          {title}
        </Typography>
        {items.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No data available.
          </Typography>
        ) : (
          items.map((item, idx) => (
            <Typography key={idx} variant="body2" sx={{ mb: 0.8 }}>
              - {item}
            </Typography>
          ))
        )}
      </Paper>
    </Grid>
  );
}

function CitationSection({
  citations,
}: {
  citations: Array<{
    title: string;
    url: string;
    publisher?: string;
    publishedAt?: string;
    note?: string;
  }>;
}) {
  return (
    <Grid item xs={12}>
      <Paper sx={{ p: 2.5, height: "100%" }}>
        <Typography variant="h6" sx={{ mb: 1.2 }}>
          Source Citations
        </Typography>
        {citations.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No citations available.
          </Typography>
        ) : (
          citations.map((citation, idx) => (
            <Box key={`${citation.url}_${idx}`} sx={{ mb: 1.2 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {idx + 1}.{" "}
                <a href={citation.url} target="_blank" rel="noreferrer">
                  {citation.title}
                </a>
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {[citation.publisher, citation.publishedAt]
                  .filter(Boolean)
                  .join(" • ")}
              </Typography>
              {citation.note && (
                <Typography
                  variant="caption"
                  display="block"
                  color="text.secondary"
                >
                  {citation.note}
                </Typography>
              )}
            </Box>
          ))
        )}
      </Paper>
    </Grid>
  );
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}
